import { IntlShape } from 'react-intl';
import { z } from 'zod';
import { isUrlValid } from '../../../../../../_metronic/helpers/AssetHelpers';
import { Vpn } from '../../../mappings';

export const schema = (intl: IntlShape) => {
  const editSiRemarksList = z.object({
    remarks: z.array(z.string()),
    others: z.array(
      z.object({
        remark: z.string(),
      })
    ),
  });

  const addBrands = z.object({
    url: z
      .string()
      .min(1)
      .refine((url) => isUrlValid(url, true), { message: 'Invalid URL' }),
    brands: z
      .array(
        z
          .object({
            sector: z.string().min(1).trim(),
            name: z.string().min(1).trim(),
            isNew: z.boolean(), // new brand
            searchType: z.string().trim(), // EXACT_MATCH | PARTIAL
            screenshot: z.preprocess(
              (value) => (value === undefined ? null : value),
              z.instanceof(File).nullable()
            ),
          })
          .refine((data) => !data.isNew || (data.isNew && data.searchType), {
            message: 'Search type is required for new brands',
            path: ['searchType'],
          })
      )
      .nonempty(),
  });

  const siAiAddBrands = z.object({
    url: z
      .string()
      .min(1)
      .refine((url) => isUrlValid(url, true), { message: 'Invalid URL' }),
    brands: z
      .array(
        z
          .object({
            sector: z.string().min(1).trim(),
            name: z.string().min(1).trim(),
            isNew: z.boolean(), // new brand
            searchType: z.string().trim(), // EXACT_MATCH | PARTIAL
            screenshot: z.instanceof(File, {
              message: intl.formatMessage({ id: 'VALIDATE_ERROR.REQUIRED' }),
            }),
          })
          .refine((data) => !data.isNew || (data.isNew && data.searchType), {
            message: 'Search type is required for new brands',
            path: ['searchType'],
          })
      )
      .nonempty(),
  });

  const categorySchema = z.object({
    category: z.string().min(1),
  });
  const sexualCategoriesSchema = z.object({
    url: z
      .string()
      .min(1)
      .refine((url) => isUrlValid(url, true), { message: 'Invalid URL' }),
    sexualCategories: z
      .array(
        z.object({
          name: z.string().min(1),
          screenshot: z.instanceof(File, {
            message: intl.formatMessage({ id: 'VALIDATE_ERROR.REQUIRED' }),
          }),
        })
      )
      .nonempty(),
  });
  const tobaccoSchema = z.object({
    url: z
      .string()
      .min(1)
      .refine((url) => isUrlValid(url, true), { message: 'Invalid URL' }),
    screenshot: z.instanceof(File, {
      message: intl.formatMessage({ id: 'VALIDATE_ERROR.REQUIRED' }),
    }),
    brands: z.array(
      z.object({
        name: z.string(),
        isNew: z.boolean(),
      })
    ),
  });
  const categorizeSchema = z.object({
    url: z
      .string()
      .min(1)
      .refine((url) => isUrlValid(url, true), { message: 'Invalid URL' }),
    screenshot: z.instanceof(File, {
      message: intl.formatMessage({ id: 'VALIDATE_ERROR.REQUIRED' }), // NOTE: replace old image
    }),
    keywords: z.array(
      z.object({
        name: z.string(),
        isNew: z.boolean(),
      })
    ),
  });

  // Data sift
  const companyNameSchema = z.object({
    companyName: z.string(),
  });
  const phoneSchema = z.object({
    phone: z.string(),
  });
  const emailSchema = z.object({
    email: z.string().email(),
  });
  const addressSchema = z.object({
    address: z.string(),
    countryCode: z.string().max(2),
    city: z.string(),
  });
  const policySchema = z.object({
    url: z.string(), // REVIEW: add url checker?
    titles: z.array(z.string()),
    // titles: z.array(z.string().min(1)).min(1),
  });

  // Inbound, Outbound, Social
  const addInboundOutboundSchema = z.object({
    links: z.array(
      z.object({
        // REVIEW: add url checker?
        foundLink: z.string().min(1),
        sourceLinks: z.string().min(1), // string of URLs delimited by comma
        title: z.string(),
        description: z.string(),
        vpns: z.array(z.nativeEnum(Vpn)).nonempty(),
      })
    ),
  });

  // Remark Option Captures
  const addRemarkOptionCapturesSchema = z.object({
    captures: z.array(
      z.object({
        url: z
          .string()
          .min(1)
          .refine((url) => isUrlValid(url, true), { message: 'Invalid URL' })
          .nullable(),
        capture_id: z.string().nullable(),
        capture: z.instanceof(File, {
          message: intl.formatMessage({ id: 'VALIDATE_ERROR.REQUIRED' }),
        }),
      })
    ),
  });

  const siAiSexualCategoriesSchema = z.object({
    url: z
      .string()
      .min(1)
      .refine((url) => isUrlValid(url, true), { message: 'Invalid URL' }),
    sexualCategories: z
      .array(
        z.object({
          name: z.string().min(1),
          screenshot: z.preprocess(
            (value) => (value === undefined ? null : value),
            z.instanceof(File).nullable()
          ),
        })
      )
      .nonempty(),
  });

  const siAiTobaccoSchema = z.object({
    url: z
      .string()
      .min(1)
      .refine((url) => isUrlValid(url, true), { message: 'Invalid URL' }),
    screenshot: z.preprocess(
      (value) => (value === undefined ? null : value),
      z.instanceof(File).nullable()
    ),
    brands: z.array(
      z.object({
        name: z.string(),
        isNew: z.boolean(),
      })
    ),
  });

  const siAiCategorizeSchema = z.object({
    url: z
      .string()
      .min(1)
      .refine((url) => isUrlValid(url, true), { message: 'Invalid URL' }),
    screenshot: z.preprocess(
      (value) => (value === undefined ? null : value),
      z.instanceof(File).nullable()
    ),
    keywords: z.array(
      z.object({
        name: z.string(),
        isNew: z.boolean(),
      })
    ),
  });

  return {
    editSiRemarksList,
    addBrands,

    categorySchema,
    sexualCategoriesSchema,
    tobaccoSchema,
    categorizeSchema,

    companyNameSchema,
    phoneSchema,
    emailSchema,
    addressSchema,
    policySchema,

    addInboundOutboundSchema,

    addRemarkOptionCapturesSchema,

    siAiAddBrands,
    siAiSexualCategoriesSchema,
    siAiTobaccoSchema,
    siAiCategorizeSchema,
  };
};
